import { GRID_DIRECTION } from "../const/grid-directions.const";
import { GridConfig } from "../modules/GridConfig";
import { CELL_STYLE } from "../components/cell/grid-cell.config";
import { getInstanceIDFromCanvasInstance } from "../../../diagram/diagram-assets/diagram-positioning-state";

export class GridSizedViewUtils {
    /**
     *
     * @param {Number} addition - The ammount that needs to be incremented for each cell
     * @param {[][]} grid - The generated grid
     * @param {string} position - Indicates if is a vertical or horizontal change. Possible values: X, Y
     */
    changeCurrentCellsPositions(addition, grid) {
        for (let row = 0; row < grid.length; row++) {
            for (let cell = 0; cell < grid[row].length; cell++) {
                const obj = {};
                obj.x = grid[row][cell].layout.x + addition.width;
                obj.y = grid[row][cell].layout.y + addition.height;
                obj.styles = CELL_STYLE;
                grid[row][cell].clearCell();
                grid[row][cell].update(obj, {}, true);
                // Not necessary since its being called from grid view before camera changes focus
                // When camera changes focus, visible grid is redrawed.
                // grid[row][cell].draw(true);
            }
        }
    }

    changeConnectorsPosition(addition, connectors) {
        connectors.forEach(connector => {
            const currentPath = Object.assign([], connector.instance.layout.path);
            for (let i = 0; i < currentPath.length; i++) {
                currentPath[i].x += addition.width;
                currentPath[i].y += addition.height;
            }
            connector.instance.update({ path: currentPath });
            connector.instance.draw();
            connector.instance.labelHandler.updateConnLabelPosExpandGrid(connector.instance);
        });
    }

    changeCellIndependentPluginsPosition(addition, plugins) {
        plugins.forEach(plugin => {
            const pluginInstance = plugin.instance;
            const newX = pluginInstance.position.x + addition.width;
            const newY = pluginInstance.position.y + addition.height;
            const toUpdate = { position: { x: newX, y: newY } };
            pluginInstance.update(toUpdate);
            pluginInstance.draw();
        });
    }

    persistTemporaryCells(grid) {
        for (let row = 0; row < grid.length; row++) {
            for (let col = grid[row].length - 1; col >= 0; col--) {
                if (grid[row][col] && grid[row][col].state.temporary) {
                    grid[row][col].update({ styles: CELL_STYLE }, { temporary: false });
                    grid[row][col].draw();
                }
            }
        }
    }

    findEmptySpace(pluginHandler, space, grid) {
        for (let row = 0; row < grid.length; row++) {
            for (let cellI = 0; cellI < grid[row].length; cellI++) {
                const cell = grid[row][cellI];
                const position = { x: cell.layout.x, y: cell.layout.y };
                const coveredCells = pluginHandler.getCoveredCells(position, space, grid, true).filter(cell => cell);
                const filteredOcupiedCells = coveredCells.filter(atCell => atCell && !atCell.isVoid() && !atCell.hasContent());
                const instanceId = getInstanceIDFromCanvasInstance(grid[0][0].ctx);
                const config = new GridConfig(instanceId);
                const verticalCells = Math.ceil(space.height / config.cellHeight);
                const horizontalCells = Math.ceil(space.width / config.cellWidth);
                if (filteredOcupiedCells.length === coveredCells.length && (verticalCells + horizontalCells) === filteredOcupiedCells.length) {
                    return cell;
                }
            }
        }
    }

    removeTemporaryCells(grid) {
        for (let row = 0; row < grid.length; row++) {
            for (let col = grid[row].length - 1; col >= 0; col--) {
                if (grid[row][col] && grid[row][col].state.temporary) {
                    grid[row][col].clearCell();
                    grid[row][col].deleteCell();
                    grid[row].splice(col, 1);
                }
            }
        }
        return grid.filter(row => row.length > 0);
    }

    getPermanentGridCellPos(pos, grid) {
        if (!pos) return null;
        const topY = grid[0][0].layout.y;
        const bottomY = grid[grid.length - 1][0].layout.y;
        const startX = grid[0][0].layout.x;
        const endX = grid[0][grid[0].length - 1].layout.x;
        const cellWidth = grid[0][0].layout.width;
        const cellHeight = grid[0][0].layout.height;
        const gridHeight = { start: topY, end: bottomY + cellHeight };
        const gridWidth = { start: startX, end: endX + cellWidth };
        const isVerticalInsideGrid = gridHeight.start < pos.y && pos.y < gridHeight.end;
        const insideHorizotalGrid = gridWidth.start < pos.x && pos.x < gridWidth.end;
        if (!isVerticalInsideGrid || !insideHorizotalGrid) {
            return null;
        }
        return {
            row: Math.floor((pos.y - topY) / cellHeight),
            col: Math.floor((pos.x - startX) / cellWidth)
        };
    }

    getTargetPoints(cell, grid, gridConfig) {
        return {
            topY: cell ? cell.layout.y : grid[0][0].layout.y,
            bottomY: cell ? cell.layout.y + gridConfig.cellHeight : grid[grid.length - 1][0].layout.y,
            startX: cell ? cell.layout.x : grid[0][0].layout.x,
            endX: cell ? cell.layout.x + gridConfig.cellWidth : grid[0][grid[0].length - 1].layout.x
        };
    }

    getGridCellsFromSubsection(startCell, endCell, grid) {
        const startPos = { x: startCell.layout.x, y: startCell.layout.y };
        const endPos = { x: endCell.layout.x, y: endCell.layout.y };
        const instanceId = getInstanceIDFromCanvasInstance(startCell.ctx);
        const gridConfig = new GridConfig(instanceId);
        const cellsToReturn = [];
        const startRowIndex = Math.floor((startPos.y - gridConfig.viewPadding) / gridConfig.cellHeight);
        const endRowIndex = Math.floor((endPos.y - gridConfig.viewPadding) / gridConfig.cellHeight);
        const startColIndex = Math.floor((startPos.x - gridConfig.viewPadding) / gridConfig.cellWidth);
        const endColIndex = Math.floor((endPos.x - gridConfig.viewPadding) / gridConfig.cellWidth);
        for (let row = startRowIndex; row <= endRowIndex; row++) {
            for (let col = startColIndex; col <= endColIndex; col++) {
                cellsToReturn.push(grid[row][col]);
            }
        }
        return cellsToReturn;
    }

    getMouseDirectionFromTarget(pos, permanentGrid, cell) {
        const directions = GRID_DIRECTION; let gridConfig; let gridHeight; let gridWidth;
        let dir;
        const instanceId = getInstanceIDFromCanvasInstance(permanentGrid ? permanentGrid[0][0].ctx : cell.ctx);
        gridConfig = new GridConfig(instanceId);
        const points = this.getTargetPoints(cell, permanentGrid, gridConfig);
        gridHeight = { start: points.topY, end: points.bottomY + (cell ? 0 : gridConfig.cellHeight) };
        gridWidth = { start: points.startX, end: points.endX + (cell ? 0 : gridConfig.cellWidth) };
        const isVerticalInsideGrid = gridHeight.start < pos.y && pos.y < gridHeight.end;
        const insideHorizotalGrid = gridWidth.start < pos.x && pos.x < gridWidth.end;

        if (pos.x < gridWidth.start && isVerticalInsideGrid) {
            dir = directions.LEFT;
        } else if (pos.x > gridWidth.end && isVerticalInsideGrid) {
            dir = directions.RIGHT;
        } else if (insideHorizotalGrid && pos.y < gridHeight.start && insideHorizotalGrid) {
            dir = directions.TOP;
        } else if (insideHorizotalGrid && pos.y > gridHeight.end) {
            dir = directions.BOTTOM;
        } else if (pos.x < gridWidth.start && pos.y < gridHeight.start) {
            dir = directions.TOP_LEFT;
        } else if (pos.x > gridWidth.end && pos.y < gridHeight.start) {
            dir = directions.TOP_RIGHT;
        } else if (pos.x > gridWidth.end && pos.y > gridHeight.end) {
            dir = directions.BOTTOM_RIGHT;
        } else if (pos.x < gridWidth.start && pos.y > gridHeight.end) {
            dir = directions.BOTTOM_LEFT;
        }
        return dir;
    }

    getMouseDirectionInsideTarget(pos, permanentGrid, cell) {
        const directions = GRID_DIRECTION;
        let dir; let gridConfig; let gridHeight; let gridWidth; let cellCenter; let diffX; let
            diffY;

        const instanceId = getInstanceIDFromCanvasInstance(permanentGrid ? permanentGrid[0][0].ctx : cell.ctx);
        gridConfig = new GridConfig(instanceId);
        const points = this.getTargetPoints(cell, permanentGrid, gridConfig);
        gridHeight = { start: points.topY, end: points.bottomY + (cell ? 0 : gridConfig.cellHeight) };
        gridWidth = { start: points.startX, end: points.endX + (cell ? 0 : gridConfig.cellWidth) };
        diffX = (gridWidth.end - gridWidth.start) / 3;
        diffY = (gridHeight.end - gridHeight.start) / 3;
        const isVerticalInsideGrid = (gridHeight.start + diffY) < pos.y && pos.y < (gridHeight.end - diffY);
        const insideHorizotalGrid = (gridWidth.start + diffX) < pos.x && pos.x < (gridWidth.end - diffX);

        cellCenter = {
            x: gridWidth.start + (gridWidth.end - gridWidth.start) / 2,
            y: gridHeight.start + (gridHeight.end - gridHeight.start) / 2
        };

        if (pos.x < cellCenter.x && isVerticalInsideGrid) {
            dir = directions.LEFT;
        } else if (pos.x > cellCenter.x && isVerticalInsideGrid) {
            dir = directions.RIGHT;
        } else if (insideHorizotalGrid && pos.y < cellCenter.y && insideHorizotalGrid) {
            dir = directions.TOP;
        } else if (insideHorizotalGrid && pos.y > cellCenter.y) {
            dir = directions.BOTTOM;
        }

        return dir;
    }

    differentCell(a, b) {
        return a && b && (a.row !== b.row || a.col !== b.col);
    }

    validCellPosition(pos, grid) {
        if (grid && grid[pos.row] && grid[pos.row][pos.col]) {
            return pos;
        }
        return null;
    }

    // Not used
    pointInRectangle(m, r) {
        const polygon = [
            [r.A.x, r.A.y],
            [r.B.x, r.B.y],
            [r.C.x, r.C.y],
            [r.D.x, r.D.y]
        ];
        return this.inside([m.x, m.y], polygon);
    }

    // Not used. Only used in above func.
    inside(point, vs) {
        // ray-casting algorithm based on
        // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html
        const x = point[0]; const
            y = point[1];

        let inside = false;
        for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
            const xi = vs[i][0]; const
                yi = vs[i][1];
            const xj = vs[j][0]; const
                yj = vs[j][1];

            const intersect = ((yi > y) != (yj > y))
                && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }

        return inside;
    }
}
